'use client';

import { useRef, useState } from 'react';
import { requestFreebie } from '@/lib/kit-tools-client';
import { Field, Honeypot, Spinner, failureMessage, inputClass } from './form-bits';

/**
 * Name and email for a gated download. The file is never linked on the page:
 * KIT emails it. The confirmation is the same whatever happened behind the
 * scenes (new request, repeat, over the hourly limit, or a caught bot), and
 * this form never branches on the response. Marketing consent is its own
 * unticked box.
 */
export function FreebieForm({ slug, cta }: { slug: string; cta: string }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [trap, setTrap] = useState('');
  const [phase, setPhase] = useState<'idle' | 'sending' | 'done'>('idle');
  const [error, setError] = useState('');
  const inFlight = useRef(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (inFlight.current) return;
    inFlight.current = true;
    setError('');
    setPhase('sending');
    try {
      await requestFreebie(slug, { name: name.trim(), email: email.trim(), marketingConsent: consent, website: trap });
      setPhase('done');
    } catch (err) {
      // Only a genuine failure (network, 4xx on a bad email, 429) reaches here; the covert outcomes all return 202.
      setPhase('idle');
      setError(failureMessage(err));
    } finally {
      inFlight.current = false;
    }
  }

  if (phase === 'done') {
    return (
      <div role="status" className="rounded-card border border-line-2 bg-veil p-6">
        <p className="font-semibold text-mist-100">Check your inbox.</p>
        <p className="mt-2 text-sm leading-relaxed text-mist-400">
          If that address is right, the guide is on its way. It can take a few minutes, and it may land in a spam or promotions folder.
        </p>
      </div>
    );
  }

  return (
    <form method="post" onSubmit={onSubmit} className="relative grid gap-5" noValidate={false}>
      <Honeypot name="website" value={trap} onChange={setTrap} />
      <Field id="freebie-name" label="Your name" required>
        <input id="freebie-name" className={inputClass} required autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field id="freebie-email" label="Email address" required hint="We email the guide here.">
        <input id="freebie-email" type="email" className={inputClass} required autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} aria-describedby="freebie-email-hint" />
      </Field>
      <label className="flex items-start gap-3 text-sm leading-relaxed text-mist-400">
        <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1 h-4 w-4 accent-[var(--color-spark-600)]" />
        <span>Also send me occasional tips by email. I can unsubscribe at any time. (Optional. You get the guide either way.)</span>
      </label>
      {error && (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      )}
      <button type="submit" disabled={phase === 'sending'} className="btn btn-primary justify-center px-7 py-3.5 text-[0.97rem] disabled:opacity-60">
        {phase === 'sending' ? <Spinner /> : null}
        {phase === 'sending' ? 'Sending' : cta}
      </button>
    </form>
  );
}
